import centroid from '@turf/centroid'
import { atom } from 'jotai'
import { atomFamily } from 'jotai/utils'
import { alphamapClient } from 'src/clients'
import {
  AddressResult,
  AMSearchRequest,
  AMSearchResults,
  AMExtendedResults,
  AMSearchResultType,
  SEARCH_RESULT_TYPE_ENUM,
  AMPoint,
  AMOwnershipParcelComplete,
} from 'src/common-services'
import { MobilityResults, NearbyResults } from 'src/server/data/alphamap/search'
import { fullAddress } from 'src/utils/full-address'
import * as coreAtoms from 'src/v2/state/core'
import { parcelFullAddress } from 'src/views/map-explorer/sidebar/panels/selected-parcel'
export const searchQuery = atom<string>('')

export const isSearching = atom(false)

// Used to specify which result types to fetch when searching
export const resultTypes = atom<AMSearchRequest['resultTypes'] | undefined>(undefined)

export const addressesResults = atom<AddressResult[] | undefined>(undefined)

// Take our address results and convert them into an array of points to fetch other alphamap data
const searchRequest = atom<Promise<AMSearchRequest | undefined>>(async (get) => {
  const addresses = get(addressesResults)
  const types = get(resultTypes)
  return addresses
    ? {
        points: addresses.map((address) => ({
          name: address.title,
          address: address.subtitle,
          point: address.meta.point,
        })),
        resultTypes: types,
      }
    : undefined
})

const extendedResults = atom<Promise<AMExtendedResults | undefined>>(async (get) => {
  const request = await get(searchRequest)
  if (!request) {
    return undefined
  }

  const results = await alphamapClient.search.getExtendedResultsFromPoints(request)
  return results
})

type FinalAddressResults = { addresses: AddressResult[]; other: AddressResult[] }
const finalAddressResults = atom<Promise<FinalAddressResults>>(async (get) => {
  // If addresses are not requested, we can return early
  const types = get(resultTypes)
  if (types && !types.includes(SEARCH_RESULT_TYPE_ENUM.Enum.address)) {
    return { addresses: [], other: [] }
  }

  // If no addresses are requested, or if extended results are not available, or if there are no points matched, we can return early
  const addresses = get(addressesResults)
  if (!addresses) {
    return { addresses: [], other: [] }
  }

  const extended = await get(extendedResults)

  // Remove any addresses that are already matched in according to the extended results
  const deduped =
    !extended || !extended.pointsMatched.length
      ? addresses
      : (addresses.filter((addr) => {
          return !extended.pointsMatched.some((matched) => matched === `${addr.title} ${addr.subtitle}`)
        }) as AddressResult[])

  // Now we want to differentiate between google address results ones that are Branded-place-like and ones that are Street-like
  // If the result's name is NOT in the address, it's a Branded-place-like else it's a Street-like
  // Branded-place-like are considered Addresses
  // Street-like are considered Other
  const addressResults = deduped.filter((addr) => {
    return !addr.subtitle?.includes(addr.title)
  })
  const otherResults = deduped.filter((addr) => {
    return addr.subtitle?.includes(addr.title)
  })

  return {
    addresses: addressResults,
    other: otherResults,
  }
})

export const searchResults = atom<Promise<AMSearchResults | undefined>>(async (get) => {
  const extended = await get(extendedResults)
  if (!extended) {
    return undefined
  }

  const deduped = await get(finalAddressResults)
  return {
    nearby: {
      ...extended.nearby,
      addresses: deduped.addresses.length ? deduped.addresses : deduped.other,
      other: deduped.addresses.length ? deduped.other : [],
    },
    byParcels: extended.byParcels,
  } as AMSearchResults
})

export const searchResultType = atom<AMSearchResultType | undefined>(undefined)

// Related results searches
// (i.e. supplementary data based on what is available in the selected entity)

export const nearbyPointFamily = atomFamily((request: AMSearchRequest) => {
  return atom<Promise<NearbyResults | undefined>>(async () => {
    const results = await alphamapClient.search.getNearbyResults(request)
    return results
  })
})

export const selectedEntityNearbyResults = atom<Promise<NearbyResults | undefined>>(async (get) => {
  const selectedEntity = await get(coreAtoms.selectedEntity)
  if (!selectedEntity) {
    return undefined
  }

  let request: AMSearchRequest | undefined = undefined
  switch (selectedEntity.type) {
    case 'SELECTED_ADDRESS':
      request = {
        points: [
          {
            name: selectedEntity.data.name,
            address: selectedEntity.data.formatted_address,
            point: {
              type: 'Point',
              coordinates: selectedEntity.data.geometry.location,
            },
          },
        ],
      }
      break
    case 'SELECTED_LOCATION':
    case 'SELECTED_SHOPPING_CENTER':
      request = {
        points: [
          {
            name: selectedEntity.data['am-loc'].name,
            address: fullAddress(selectedEntity.data['am-loc']),
            point: selectedEntity.data['am-loc'].latLng,
          },
        ],
      }
      break
    case 'SELECTED_PARCEL':
      request = {
        points: [
          {
            name: selectedEntity.data.address || '',
            address: selectedEntity.data.address || '',
            point: centroid(selectedEntity.data.geometry).geometry,
          },
        ],
      }
      break
    default:
      return undefined
  }

  if (!request) {
    return undefined
  }

  request.resultTypes = [SEARCH_RESULT_TYPE_ENUM.Enum.location, SEARCH_RESULT_TYPE_ENUM.Enum['shopping center']]

  const results = await get(nearbyPointFamily(request))
  return results
})

export const selectedEntityParcelResults = atom<Promise<MobilityResults | undefined>>(async (get) => {
  const selectedEntity = await get(coreAtoms.selectedEntity)
  if (!selectedEntity) {
    return undefined
  }

  let request: AMSearchRequest | undefined = undefined
  switch (selectedEntity.type) {
    case 'SELECTED_ADDRESS':
      request = {
        points: [
          {
            name: selectedEntity.data.name,
            address: selectedEntity.data.formatted_address,
            point: {
              type: 'Point',
              coordinates: selectedEntity.data.geometry.location,
            },
          },
        ],
      }
      break
    case 'SELECTED_LOCATION':
    case 'SELECTED_SHOPPING_CENTER':
      request = {
        points: [
          {
            name: selectedEntity.data['am-loc'].name,
            address: fullAddress(selectedEntity.data['am-loc']),
            point: selectedEntity.data['am-loc'].latLng,
          },
        ],
      }
      break
    case 'SELECTED_PARCEL':
      request = {
        points: [
          {
            name: selectedEntity.data.address || '',
            address: parcelFullAddress(
              selectedEntity.data.address || '',
              selectedEntity.data.city || '',
              selectedEntity.data.state || '',
              selectedEntity.data.zip || ''
            ),
            point: centroid(selectedEntity.data.geometry).geometry,
          },
        ],
      }
      break
    default:
      return undefined
  }

  if (!request) {
    return undefined
  }

  request.resultTypes = [SEARCH_RESULT_TYPE_ENUM.Enum.location, SEARCH_RESULT_TYPE_ENUM.Enum['shopping center']]
  const results = await get(parcelResults(request))
  return results
})

export const parcelResults = atomFamily((request: AMSearchRequest) => {
  return atom<Promise<MobilityResults | undefined>>(async () => {
    const results = await alphamapClient.search.getParcelResults(request)
    return results
  })
})

export const selectedEntityParcel = atom<Promise<AMOwnershipParcelComplete | undefined>>(async (get) => {
  const selectedEntity = await get(coreAtoms.selectedEntity)
  if (!selectedEntity) {
    return undefined
  }

  if (selectedEntity.type === 'SELECTED_PARCEL') {
    return selectedEntity.data
  }

  let point: AMPoint | undefined = undefined
  switch (selectedEntity.type) {
    case 'SELECTED_ADDRESS':
      point = {
        type: 'Point',
        coordinates: selectedEntity.data.geometry.location,
      }
      break
    case 'SELECTED_LOCATION':
    case 'SELECTED_SHOPPING_CENTER':
      point = selectedEntity.data['am-loc'].latLng
      break

    default:
      return undefined
  }

  const results = await alphamapClient.ownership.getParcelByPoint(point)
  return results
})
